import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Paintbrush, SlidersHorizontal, Truck, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import {
  useSeo, HERO_IMAGE, getPortfolio, getFaqs, getReviews, getHomepageContent, HOME_DEFAULTS,
  type SiteSettings, type PortfolioProject, type Faq, type Review, type HomeSections,
} from "@/lib/publicContent";

// Icons stay fixed in code (only texts/images are CMS-editable).
const STEP_ICONS = [SlidersHorizontal, Paintbrush, Truck];

export default function HomePage() {
  const settings = useOutletContext<SiteSettings | null>();
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [home, setHome] = useState<HomeSections>(() => {
    const d: HomeSections = {};
    for (const k of Object.keys(HOME_DEFAULTS)) d[k] = { ...HOME_DEFAULTS[k] };
    return d;
  });

  const hero = home.hero;
  const how = home.how;
  const pf = home.portfolio;
  const rv = home.reviews;
  const fq = home.faq;
  const cta = home.cta;
  const promo = home.promo;
  const heroImage = hero.image_url || HERO_IMAGE;
  const tickerItems: string[] = home.ticker.items || [];
  const steps: { title: string; text: string }[] = how.steps || [];

  useSeo({
    title: settings?.seo_title || "Bonker — Sneaker Personalizzate",
    description: settings?.seo_description || "Sneaker custom dipinte a mano, urban e uniche. Configura la tua.",
    image: mediaUrl(heroImage),
  });

  useEffect(() => {
    getHomepageContent().then(setHome).catch(() => {});
    getPortfolio().then((p) => setProjects(p.slice(0, 6))).catch(() => {});
    getFaqs().then((f) => setFaqs(f.slice(0, 5))).catch(() => {});
    getReviews().then(setReviews).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bonker-noise" aria-labelledby="hero-heading">
        <div className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-primary/20 blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-52 -left-40 h-[32rem] w-[32rem] rounded-full bg-secondary/10 blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block text-xs uppercase tracking-[0.25em] font-bold text-secondary mb-6" data-testid="hero-eyebrow">
              {hero.eyebrow}
            </span>
            <h1 id="hero-heading" data-testid="hero-heading" className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter">
              {hero.title_line1}<br /><span className="text-primary">{hero.title_line2}<span className="text-secondary">.</span></span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-7 max-w-md leading-relaxed">
              {hero.paragraph}
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/configuratore" data-testid="hero-cta-configura" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 py-4 hover:-translate-y-[2px] transition-transform">
                {hero.cta_primary} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-portfolio" className="inline-flex items-center gap-2 border border-white/20 text-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 py-4 hover:border-secondary hover:text-secondary transition-colors">
                {hero.cta_secondary}
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <div className="absolute inset-0 translate-x-4 translate-y-4 border-2 border-secondary hidden sm:block" aria-hidden="true" />
            <div className="relative bg-white/5 border border-white/10 overflow-hidden">
              <img src={mediaUrl(heroImage)} alt="Sneaker Air Jordan custom dipinta a mano con artwork anime" className="w-full h-auto object-cover" data-testid="hero-image" loading="eager" />
            </div>
            <div className="absolute bottom-4 left-4 bg-secondary text-black font-display font-black text-sm px-4 py-2 uppercase tracking-wider">
              {hero.badge}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TICKER */}
      <div className="bg-secondary text-black py-3 overflow-hidden border-y-2 border-black" aria-hidden="true">
        <div className="flex gap-8 whitespace-nowrap animate-marquee">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="text-sm font-display font-black uppercase tracking-wider flex items-center gap-8">
              {t} <span className="text-black/50">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* COME FUNZIONA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28" aria-labelledby="how-heading">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-primary">{how.eyebrow}</span>
            <h2 id="how-heading" className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">{how.title_line1}<br />{how.title_line2}</h2>
          </div>
          <p className="text-muted-foreground max-w-sm text-sm md:text-base">{how.description}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[i] ?? STEP_ICONS[STEP_ICONS.length - 1];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.1 }}
                className="border border-white/10 p-8 bg-card hover:border-secondary/50 transition-colors group" data-testid={`step-card-${i}`}>
                <div className="flex items-center justify-between mb-8">
                  <span className="font-display font-black text-5xl text-white/10 group-hover:text-secondary/30 transition-colors">0{i + 1}</span>
                  <Icon className="h-7 w-7 text-secondary" />
                </div>
                <h3 className="font-display font-bold text-xl mb-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PROMO — occasioni speciali / aziende (compatto, opzionale via CMS) */}
      {promo && Array.isArray(promo.cards) && promo.cards.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="home-promo" aria-labelledby="promo-heading">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              {promo.eyebrow && <span className="text-xs uppercase tracking-[0.25em] font-bold text-secondary">{promo.eyebrow}</span>}
              <h2 id="promo-heading" className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2">{promo.title}</h2>
            </div>
            {promo.text && <p className="text-muted-foreground max-w-sm text-sm">{promo.text}</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {promo.cards.map((c: any, i: number) => <PromoCard key={i} c={c} idx={i} />)}
          </div>
        </section>
      )}

      {/* PORTFOLIO PREVIEW */}
      <section className="bg-black relative bonker-noise" aria-labelledby="work-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 relative z-10">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-secondary">{pf.eyebrow}</span>
              <h2 id="work-heading" className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">{pf.title}</h2>
            </div>
            <Link to="/portfolio" className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-secondary transition-colors">
              {pf.link_label} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          {projects.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-portfolio-grid">
              {projects.map((p) => <ProjectCard key={p.id} p={p} />)}
            </div>
          ) : (
            <div className="border border-dashed border-white/15 p-16 text-center" data-testid="home-portfolio-empty">
              <p className="font-display font-bold text-2xl mb-2">{pf.empty_title}</p>
              <p className="text-muted-foreground mb-8">{pf.empty_text}</p>
              <Link to="/configuratore" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-7 py-4 hover:-translate-y-[2px] transition-transform">
                {pf.empty_cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && <ReviewsCarousel reviews={reviews} title={rv.title} />}

      {/* FAQ PREVIEW */}
      {faqs.length > 0 && (
        <section className="bg-black" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-5 md:px-10 py-20 md:py-28">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-secondary">{fq.eyebrow}</span>
              <h2 id="faq-heading" className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">{fq.title}</h2>
            </div>
            <div className="divide-y divide-white/10 border-y border-white/10" data-testid="home-faq">
              {faqs.map((f) => (
                <details key={f.id} className="group py-5">
                  <summary className="flex items-center justify-between cursor-pointer list-none font-display font-bold text-lg">
                    {f.question}
                    <span className="text-secondary text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.answer}</p>
                </details>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/faq" className="text-sm uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-secondary transition-colors">{fq.link_label}</Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-primary" aria-labelledby="cta-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 text-center relative z-10">
          <h2 id="cta-heading" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-primary-foreground">
            {cta.title_line1}<br />{cta.title_line2}
          </h2>
          <Link to="/configuratore" data-testid="cta-band-configura" className="inline-flex items-center gap-2 bg-black text-white font-bold uppercase tracking-[0.15em] text-sm px-10 py-5 mt-9 hover:-translate-y-[2px] transition-transform">
            {cta.cta_label} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

const ProjectCard: React.FC<{ p: PortfolioProject }> = ({ p }) => (
  <Link to="/portfolio" className="group block relative overflow-hidden border border-white/10 bg-card aspect-[4/5]" data-testid="project-card">
    {p.cover_image_url ? (
      <img src={mediaUrl(p.cover_image_url)} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-white/5"><span className="font-display font-black text-4xl text-white/10">{p.title.slice(0, 2).toUpperCase()}</span></div>
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-5">
      <h3 className="font-display font-bold text-lg">{p.title}</h3>
      {p.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-1">{p.description}</p>}
    </div>
  </Link>
);


const PromoCard: React.FC<{ c: any; idx: number }> = ({ c, idx }) => {
  const internal = (c.button_link || "").startsWith("/");
  const inner = (
    <div className="group relative border border-white/10 bg-card p-7 hover:border-secondary/50 transition-colors overflow-hidden flex flex-col h-full">
      {c.image_url && (
        <div className="mb-5 -mx-7 -mt-7 aspect-[16/9] overflow-hidden">
          <img src={mediaUrl(c.image_url)} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        </div>
      )}
      <h3 className="font-display font-bold text-xl">{c.title}</h3>
      {c.text && <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{c.text}</p>}
      <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-secondary">
        {c.button_text || "Scopri"} <ArrowUpRight className="h-4 w-4" />
      </span>
    </div>
  );
  return internal ? (
    <Link to={c.button_link || "/"} data-testid={`promo-card-${idx}`} className="block h-full">{inner}</Link>
  ) : (
    <a href={c.button_link || "#"} target="_blank" rel="noreferrer" data-testid={`promo-card-${idx}`} className="block h-full">{inner}</a>
  );
};

const ReviewsCarousel: React.FC<{ reviews: Review[]; title: string }> = ({ reviews, title }) => {
  const [[index, dir], setSlide] = useState<[number, number]>([0, 0]);
  const count = reviews.length;
  const go = (d: number) => setSlide(([i]) => [(i + d + count) % count, d]);
  const r = reviews[index];

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28" aria-labelledby="reviews-heading">
      <h2 id="reviews-heading" className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-12 text-center">{title}</h2>
      <div className="relative max-w-2xl mx-auto" data-testid="home-reviews">
        <div className="overflow-hidden">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={r.id}
              custom={dir}
              initial={{ opacity: 0, x: dir >= 0 ? 60 : -60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir >= 0 ? -60 : 60 }}
              transition={{ duration: 0.3 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}
              className="border border-white/10 p-8 md:p-10 bg-card cursor-grab active:cursor-grabbing select-none min-h-[220px] flex flex-col justify-center"
              data-testid="review-card"
            >
              <Quote className="h-6 w-6 text-secondary mb-5" />
              <p className="text-base md:text-lg leading-relaxed mb-6">{r.text}</p>
              <span className="font-bold text-sm" data-testid="review-author">{r.author_name}</span>
            </motion.div>
          </AnimatePresence>
        </div>

        {count > 1 && (
          <>
            {/* Desktop: frecce esterne alla card */}
            <button data-testid="review-prev" onClick={() => go(-1)} aria-label="Testimonianza precedente"
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-5 h-11 w-11 items-center justify-center bg-background border border-white/15 hover:border-secondary hover:text-secondary transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button data-testid="review-next" onClick={() => go(1)} aria-label="Testimonianza successiva"
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-5 h-11 w-11 items-center justify-center bg-background border border-white/15 hover:border-secondary hover:text-secondary transition-colors">
              <ChevronRight className="h-5 w-5" />
            </button>
            {/* Controlli: su mobile le frecce stanno sotto la card, mai sopra il testo */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button data-testid="review-prev-mobile" onClick={() => go(-1)} aria-label="Testimonianza precedente"
                className="md:hidden h-9 w-9 flex items-center justify-center bg-background border border-white/15 hover:border-secondary hover:text-secondary transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex justify-center gap-2" data-testid="review-dots">
                {reviews.map((_, i) => (
                  <button key={i} aria-label={`Vai alla testimonianza ${i + 1}`}
                    onClick={() => setSlide([i, i > index ? 1 : -1])}
                    className={`h-1.5 transition-all ${i === index ? "w-6 bg-secondary" : "w-1.5 bg-white/25 hover:bg-white/50"}`} />
                ))}
              </div>
              <button data-testid="review-next-mobile" onClick={() => go(1)} aria-label="Testimonianza successiva"
                className="md:hidden h-9 w-9 flex items-center justify-center bg-background border border-white/15 hover:border-secondary hover:text-secondary transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
